import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import React, { ReactNode } from 'react'
import { CheckIcon, ChevronRightIcon } from '@heroicons/react/24/solid'
import { Tooltip } from './Tooltip'

type Props = {
  trigger: ReactNode
  items: DropdownItem[]
  align?: 'start' | 'end'
}

type BaseDropdownItem = {
  icon?: ReactNode
  text: string
  disabled?: boolean
}

type SharedActionAttrs = {
  shortcut?: string
  variant?: 'default' | 'danger'
  size?: 'default' | 'large'
  description?: string
  tooltip?: string
}

type LinkAction = BaseDropdownItem &
  SharedActionAttrs & {
    href: string
  }

type ClickAction = BaseDropdownItem &
  SharedActionAttrs & {
    checked?: boolean
    onClick: () => void
  }

type Action = LinkAction | ClickAction

type Submenu = BaseDropdownItem & {
  submenuItems: DropdownItem[]
}

export type DropdownItem = Action | Submenu

export function Dropdown({ trigger, items, align = 'start' }: Props) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger className="group/trigger rounded-lg max-w-full flex-auto">{trigger}</DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="min-w-[220px] bg-primary rounded-md shadow-xl p-1 z-50 flex flex-col gap-3"
          sideOffset={5}
          align={align}
        >
          {items.map((item, i) => (
            <DropdownItemComponent key={i} item={item} />
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

function DropdownItemComponent({ item }: { item: DropdownItem }) {
  return 'submenuItems' in item ? <SubmenuItem item={item} /> : <SingleItem item={item} />
}

function SubmenuItem({ item }: { item: Submenu }) {
  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger
        className="flex items-center px-2 py-1.5 text-sm outline-none hover:bg-interactive-background-action-current rounded cursor-default"
        disabled={item.disabled}
      >
        {item.icon && <span className="mr-2 h-4 w-4">{item.icon}</span>}
        <span className="grow">{item.text}</span>
        <ChevronRightIcon className="h-4 w-4" />
      </DropdownMenu.SubTrigger>

      <DropdownMenu.Portal>
        <DropdownMenu.SubContent
          className="min-w-[220px] bg-primary rounded-md shadow-lg p-1"
          sideOffset={2}
          alignOffset={-5}
        >
          {item.submenuItems.map((subItem, i) => (
            <DropdownItemComponent key={i} item={subItem} />
          ))}
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  )
}

function SingleItem({ item }: { item: Action }) {
  const isLarge = item.size === 'large'

  const linkAttrs =
    'href' in item && item.href.startsWith('/')
      ? {
          'data-phx-link': 'redirect',
          'data-phx-link-state': 'push',
          target: '_self',
        }
      : {}

  return (
    <DropdownMenu.Item
      className={`
        px-2 py-1.5 text-sm outline-none rounded cursor-default
        ${item.variant === 'danger' ? 'hover:bg-red-50' : 'checked' in item && item.checked ? 'bg-interactive-background-action-current' : 'hover:bg-slate-100'}
        ${item.disabled ? 'opacity-50' : ''}
        ${isLarge ? 'min-h-12' : ''}
      `}
      disabled={item.disabled}
      onSelect={'onClick' in item ? item.onClick : undefined}
    >
      {'href' in item ? (
        <a href={item.href} {...linkAttrs}>
          <ItemBody item={item} />
        </a>
      ) : (
        <ItemBody item={item} />
      )}
    </DropdownMenu.Item>
  )
}

function ItemBody({ item }: { item: Action }) {
  const isMultiLine = !!item.description
  const textColor = item.variant === 'danger' ? 'text-danger' : 'text-primary'
  const isLarge = item.size === 'large'
  const textWeight = isLarge ? 'font-normal' : 'font-medium'

  return (
    <Tooltip tooltip={item.tooltip}>
      <div className="flex items-center w-full">
        {item.icon && <span className="mr-2">{item.icon}</span>}
        {isMultiLine ? (
          <div className="flex flex-col grow">
            <span className={`grow ${textColor} ${textWeight}`}>{item.text}</span>
            <span className="text-xs text-secondary">{item.description}</span>
          </div>
        ) : (
          <>
            <span className={`grow ${textColor} ${textWeight}`}>{item.text}</span>
          </>
        )}
        {item.shortcut && <span className="ml-4 text-sm text-secondary">{item.shortcut}</span>}
        {'checked' in item && <RadioButton checked={item.checked} />}
      </div>
    </Tooltip>
  )
}

const RadioButton = ({ checked }: { checked: boolean | undefined }) => (
  <span
    className={
      'inline-block ml-2 border border-secondary-inverse rounded-full w-6 h-6' +
      (checked ? ' bg-primary-inverse flex items-center justify-center' : '')
    }
  >
    {checked ? <CheckIcon className="h-4 w-4 icon-primary text-primary-inverse" /> : <>&nbsp;</>}
  </span>
)
